"use client";

import React, { useEffect, useRef, useState } from "react";

/**
 * LayeredSlider — a large, layered, auto-advancing photo hero visual.
 *
 * Depth layers (best-practice composition):
 *   1. Main card — big crossfading photo with a slow Ken-Burns zoom on
 *      the active slide, a slide counter, and an autoplay progress bar.
 *   2. An overlapping tilted card showing the next photo (parallax depth).
 *   3. A floating pill badge.
 *   4. Clickable dots beneath the card.
 *
 * Autoplay (5s) pauses on hover; honours prefers-reduced-motion.
 */
export function LayeredSlider({
  photos,
  badge,
  autoplay = true,
  intervalMs = 5000,
}: {
  photos: string[];
  badge?: string;
  autoplay?: boolean;
  intervalMs?: number;
}) {
  const [active, setActive] = useState(0);
  const hovering = useRef(false);
  const n = photos.length;
  const DURATION = Math.max(2000, intervalMs);

  useEffect(() => {
    if (n <= 1 || !autoplay) return;
    const id = setInterval(() => {
      if (!hovering.current) setActive((p) => (p + 1) % n);
    }, DURATION);
    return () => clearInterval(id);
  }, [n, autoplay, DURATION]);

  if (n === 0) return null;
  const secondary = photos[(active + 1) % n];
  const pad2 = (x: number) => String(x).padStart(2, "0");

  return (
    <div
      className="um-slider group relative w-full max-w-[36rem]"
      onMouseEnter={() => (hovering.current = true)}
      onMouseLeave={() => (hovering.current = false)}
    >
      {/* soft glow behind the stack */}
      <div
        aria-hidden="true"
        className="absolute -inset-8 rounded-[3rem]"
        style={{ background: "rgba(255,255,255,0.07)", filter: "blur(14px)" }}
      />

      {/* main crossfade card */}
      <div className="relative aspect-[5/6] w-full overflow-hidden rounded-[2rem] shadow-2xl ring-1 ring-white/20">
        {photos.map((src, idx) => (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={idx}
            src={src}
            alt=""
            loading={idx === 0 ? "eager" : "lazy"}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1000ms] ease-in-out ${
              idx === active ? "opacity-100 um-kb" : "opacity-0"
            }`}
          />
        ))}

        {/* bottom scrim */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.45), transparent 50%)",
          }}
        />

        {/* slide counter */}
        <div className="absolute left-4 top-4 rounded-full bg-black/35 px-3 py-1 text-[11px] font-semibold tracking-[0.2em] text-white backdrop-blur-sm">
          {pad2(active + 1)} / {pad2(n)}
        </div>

        {/* autoplay progress bar (restarts each slide via key) */}
        {autoplay && n > 1 ? (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-white/25">
            <div
              key={active}
              className="um-prog h-full bg-white"
              style={{ animationDuration: `${DURATION}ms` }}
            />
          </div>
        ) : null}
      </div>

      {/* dots beneath the card */}
      <div className="mt-5 flex justify-center gap-2">
        {photos.map((_, idx) => (
          <button
            key={idx}
            type="button"
            aria-label={`Show photo ${idx + 1}`}
            onClick={() => setActive(idx)}
            className="h-2 rounded-full transition-all duration-300"
            style={{
              width: idx === active ? "26px" : "8px",
              background:
                idx === active ? "#FFFFFF" : "rgba(255,255,255,0.5)",
            }}
          />
        ))}
      </div>

      {/* overlapping secondary card (depth layer) */}
      <div
        className="absolute -bottom-6 -left-10 hidden h-40 w-56 overflow-hidden rounded-2xl shadow-2xl ring-4 ring-white/90 sm:block"
        style={{ transform: "rotate(-5deg)" }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={secondary}
          alt=""
          className="h-full w-full object-cover transition-opacity duration-700"
        />
      </div>

      {/* floating pill badge */}
      {badge ? (
        <div
          className="absolute -top-5 right-6 rounded-full bg-white px-5 py-2.5 text-sm font-bold tracking-wide shadow-xl"
          style={{ color: "var(--color-primary)" }}
        >
          {badge}
        </div>
      ) : null}

      <style>{`
        @keyframes um-kb { from { transform: scale(1.02); } to { transform: scale(1.12); } }
        @keyframes um-prog { from { width: 0%; } to { width: 100%; } }
        .um-kb { animation: um-kb ${DURATION}ms ease-out both; transform-origin: center; }
        .um-prog { animation: um-prog ${DURATION}ms linear both; }
        .um-slider:hover .um-kb, .um-slider:hover .um-prog { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .um-kb { animation: none; }
          .um-prog { animation: none; width: 100%; }
        }
      `}</style>
    </div>
  );
}

export default LayeredSlider;
